/**
 * Lazy loader for the Reviews.io widgets.
 *
 * Why this exists
 * ---------------
 * Reviews.io ships its widgets as parser-blocking `<script src>` tags plus two
 * remote stylesheets, dropped in mid-body by the sections that use them. On the G3
 * PDP that meant two third-party scripts and two third-party stylesheets blocking
 * the parser for sections sitting at position 11 and 16 of 18, i.e. far below the
 * fold, on a page whose payload was already 30 MB and whose CrUX INP was failing.
 *
 * Nothing about those widgets needs to exist before the visitor gets near them.
 * This module defers the whole bundle until the widget's own container approaches
 * the viewport, then hands control back to the section that queued it.
 *
 * How sections use it
 * -------------------
 * The loader is a deferred asset, so it executes after the document is parsed and
 * therefore after the inline section scripts. Sections cannot call it directly;
 * they push a job onto a queue instead, and the loader drains it:
 *
 *   (window.zhRioQueue = window.zhRioQueue || []).push({
 *     target: 'reviewsio-carousel-widget-abc',   // element id
 *     assets: [['https://…/dist.js', 'js'], ['https://…/widget.css', 'css']],
 *     global: 'carouselInlineWidget',            // constructor to wait for
 *     init: function () { … }                    // runs once that global exists
 *   });
 *
 * After the loader has run, later pushes are executed immediately, so a section
 * rendered by the theme editor through shopify:section:load still works. The queue
 * stays a real Array with an overridden push, rather than being replaced by a
 * lookalike object: anything that probes it with Array.isArray or pushes several
 * jobs at once keeps working.
 *
 * Assets are injected at most once per URL across all widgets on the page, which
 * matters because a PDP can carry three Reviews.io widgets that share dist.js and
 * the icon font stylesheet.
 *
 * Why not reuse data-zh-plp-lazy-script in assets/zh-plp.js
 * -------------------------------------------------------
 * That one arms a self-mounting script (sentiment.js mounts itself) and does three
 * things this case needs differently: it cannot inject stylesheets, it has no way
 * to wait for a constructor and hand control back to the section, and it does not
 * dedupe a URL shared by several widgets. It also lives inside zh-plp.js, which the
 * Reviews.io sections do not otherwise load. Keeping them separate is deliberate;
 * if a third case turns up, merging them is the better move.
 *
 * No Subresource Integrity on these tags, deliberately. Reviews.io ships mutable,
 * cache-busted bundles it updates on its own schedule, so a pinned hash would turn
 * a vendor release into a dead widget. The tags were unpinned before this change
 * too, so nothing is given up here.
 *
 * Run once per page. Both Reviews.io sections carry their own <script src> for this
 * file, and a classic script executes as often as it is included. A second run
 * would replace the queue's push with one bound to a fresh, empty URL registry, so
 * a later widget could inject dist.js a second time.
 */
(function () {
  'use strict';

  if (window.__zhRioLoaderInit) return;
  window.__zhRioLoaderInit = true;

  /* One viewport and a half of lead time. The widgets fetch their reviews over the
     network after the bundle lands, so warming them at the last moment would trade
     a blocked parser for a visible empty box. Matches the warming distance used by
     assets/zh-img-warm.js. */
  var ROOT_MARGIN = '1200px 0px';

  /* Backstop only. The constructor is normally picked up from the script's own load
     event; this covers a bundle that loads but defines its global asynchronously.
     Generous on purpose: the previous parser-blocking tags always arrived
     eventually, and a widget that quietly gives up on a slow phone would be a
     regression for exactly the visitors this whole change is for. */
  var POLL_MS = 250;
  var POLL_TRIES = 120;

  var injected = Object.create(null);

  function makeTag(url, kind) {
    var el;
    if (kind === 'css') {
      el = document.createElement('link');
      el.rel = 'stylesheet';
      el.href = url;
    } else {
      el = document.createElement('script');
      el.src = url;
      el.async = true;
    }
    return el;
  }

  /** Injects a URL once and resolves when that tag reports load (or failure). */
  function inject(url, kind, onSettled) {
    var entry = injected[url];
    if (entry) {
      if (entry.settled) onSettled();
      else entry.waiting.push(onSettled);
      return;
    }

    entry = injected[url] = { settled: false, waiting: [onSettled] };

    function settle() {
      if (entry.settled) return;
      entry.settled = true;
      var waiting = entry.waiting;
      entry.waiting = [];
      for (var i = 0; i < waiting.length; i++) waiting[i]();
    }

    var el = makeTag(url, kind);
    el.addEventListener('load', settle);
    el.addEventListener('error', settle);
    document.head.appendChild(el);
  }

  function whenGlobal(name, cb) {
    var tries = 0;

    function attempt() {
      if (typeof window[name] !== 'undefined') {
        /* Never let a widget init error bubble into the page. */
        try {
          cb();
        } catch (e) {}
        return;
      }
      if (++tries > POLL_TRIES) return;
      setTimeout(attempt, POLL_MS);
    }

    attempt();
  }

  function start(job) {
    var assets = job.assets || [];
    var pending = assets.length;

    if (!pending) {
      whenGlobal(job.global, job.init);
      return;
    }

    function onSettled() {
      if (--pending > 0) return;
      /* The script has reported load, so the constructor is normally already there
         and whenGlobal returns on its first synchronous attempt. */
      whenGlobal(job.global, job.init);
    }

    for (var i = 0; i < assets.length; i++) {
      inject(assets[i][0], assets[i][1], onSettled);
    }
  }

  function onIntersect(entries, observer, job) {
    for (var i = 0; i < entries.length; i++) {
      if (!entries[i].isIntersecting) continue;
      observer.disconnect();
      start(job);
      return;
    }
  }

  function run(job) {
    if (!job || typeof job.init !== 'function') return;

    var target = document.getElementById(job.target);

    /* No container and no IntersectionObserver both mean we cannot make a sensible
       visibility decision. Fall back to loading right away: slower, but correct. */
    if (!target || typeof IntersectionObserver !== 'function') {
      start(job);
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      onIntersect(entries, observer, job);
    }, { rootMargin: ROOT_MARGIN });

    observer.observe(target);
  }

  var queue = (window.zhRioQueue = window.zhRioQueue || []);
  while (queue.length) run(queue.shift());

  /* Keep the Array, override push: anything that probes the queue with
     Array.isArray, or pushes several jobs in one call, keeps working. */
  queue.push = function () {
    for (var i = 0; i < arguments.length; i++) run(arguments[i]);
    return 0;
  };
})();
